// BotSettings.tsx — dashboard bot Settings screen (M4 task-6, route
// /bots/:botId/settings). Edits `BotSettings.allowedDomains` + `.leadCapture`
// (packages/shared-types/settings.ts) and top-level `Bot.personaNote` via
// `PATCH /api/bots/:id { settings: {...}, personaNote }` — replace-per-top-level-key
// semantics for `settings` (apps/api/src/routes/bots.ts): this always sends all three
// together since they're edited on the same form. `botSettingsSchema` (packages/validation)
// parses `BotDto.settings` (`unknown` on the wire) into typed defaults client-side, the same
// way the server does, so the two can never drift. The Danger zone (bot delete) is a
// separate `DangerZone` component below — its own dialog, its own mutation, spec §3.
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { botSettingsSchema } from '@repo/validation';
import { Button } from '@repo/ui/components/button';
import { Input } from '@repo/ui/components/input';
import { Label } from '@repo/ui/components/label';
import { Skeleton, SkeletonText } from '@repo/ui/components/skeleton';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@repo/ui/components/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@repo/ui/components/select';
import type { BotDto, LeadCaptureMode, LeadProactivenessLevel } from '@repo/shared-types';
import { EmbedSnippet } from '../components/EmbedSnippet';
import { NotificationsCard } from '../components/NotificationsCard';
import { PageHeader } from '../components/PageHeader';
import { apiFetch } from '../lib/api';
import { landingUrl } from '../lib/landing-url';
import { useT } from '../locales/context';
import type { Catalog } from '../locales/en';

const PERSONA_NOTE_MAX = 500;

function getLeadCaptureOptions(t: Catalog): { value: LeadCaptureMode; label: string; blurb: string }[] {
  return [
    { value: 'off', label: t.botSettings.leadCaptureOffLabel, blurb: t.botSettings.leadCaptureOffBlurb },
    {
      value: 'on-intent',
      label: t.botSettings.leadCaptureOnIntentLabel,
      blurb: t.botSettings.leadCaptureOnIntentBlurb,
    },
    {
      value: 'always',
      label: t.botSettings.leadCaptureAlwaysLabel,
      blurb: t.botSettings.leadCaptureAlwaysBlurb,
    },
    {
      value: 'pre-chat',
      label: t.botSettings.leadCapturePreChatLabel,
      blurb: t.botSettings.leadCapturePreChatBlurb,
    },
  ];
}

function getLeadLevelOptions(t: Catalog): { value: LeadProactivenessLevel; label: string; blurb: string }[] {
  return [
    {
      value: 'subtle',
      label: t.botSettings.leadLevelSubtleLabel,
      blurb: t.botSettings.leadLevelSubtleBlurb,
    },
    {
      value: 'balanced',
      label: t.botSettings.leadLevelBalancedLabel,
      blurb: t.botSettings.leadLevelBalancedBlurb,
    },
    {
      value: 'proactive',
      label: t.botSettings.leadLevelProactiveLabel,
      blurb: t.botSettings.leadLevelProactiveBlurb,
    },
  ];
}

/** Mirrors `hostnameSchema` in packages/validation/src/bot-settings.ts — bare hostnames
 * only, no scheme, no path. Kept as a light client-side check so a bad entry is caught
 * before the round trip; the server re-validates the same shape regardless. */
function validateHostname(value: string, t: Catalog): string | null {
  const trimmed = value.trim();
  if (!trimmed) return t.botSettings.domainRequiredError;
  if (trimmed.includes('://') || trimmed.includes('/')) {
    return t.botSettings.domainBareHostnameError;
  }
  if (trimmed.length > 253) return t.botSettings.domainTooLongError;
  return null;
}

/** Sized to `SettingsForm`'s real field blocks below — About/Allowed domains/Lead capture,
 * then the Embed snippet and Danger zone cards (handoff Shell Anatomy §06). */
function SettingsSkeleton() {
  return (
    <div className="flex max-w-2xl flex-col gap-6">
      <div className="flex flex-col gap-6 rounded-xl border p-4">
        {['w-24', 'w-32', 'w-28'].map((w) => (
          <div key={w} className="flex flex-col gap-2">
            <SkeletonText className={w} />
            <Skeleton className="h-9 w-full" />
          </div>
        ))}
      </div>
      <Skeleton className="h-24 w-full rounded-xl" />
    </div>
  );
}

export function BotSettings() {
  const { botId } = useParams<{ botId: string }>();

  const {
    data: bot,
    isPending: botPending,
    error: botFetchError,
  } = useQuery({
    queryKey: ['bots', botId],
    queryFn: () => apiFetch<BotDto>(`/api/bots/${botId}`),
    enabled: !!botId,
  });

  const t = useT();

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title={t.botSettings.pageTitle} subtitle={t.botSettings.pageSubtitle} />

      {botPending && <SettingsSkeleton />}
      {botFetchError && <p className="text-destructive">{botFetchError.message}</p>}

      {/* `key={bot.id}` — see the identical comment in BotAppearance.tsx: initial state
          derives from `bot.settings` via lazy useState initializers below, once per bot,
          with no effect required and no risk of a background refetch clobbering edits. */}
      {bot && <SettingsForm key={bot.id} botId={botId} bot={bot} />}
    </div>
  );
}

function SettingsForm({ botId, bot }: { botId: string | undefined; bot: BotDto }) {
  const t = useT();
  const queryClient = useQueryClient();
  const initial = botSettingsSchema.parse(bot.settings ?? {});
  const leadCaptureOptions = getLeadCaptureOptions(t);
  const leadLevelOptions = getLeadLevelOptions(t);

  const [domains, setDomains] = useState<string[]>(initial.allowedDomains);
  const [leadCapture, setLeadCapture] = useState<LeadCaptureMode>(initial.leadCapture);
  const [leadLevel, setLeadLevel] = useState<LeadProactivenessLevel>(initial.leadLevel);
  const [personaNote, setPersonaNote] = useState<string>(bot.personaNote ?? '');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const domainErrors = domains.map((d) => validateHostname(d, t));
  const hasDomainErrors = domainErrors.some((e) => e !== null);

  function updateDomain(index: number, value: string) {
    setDomains((prev) => prev.map((d, i) => (i === index ? value : d)));
  }

  function removeDomain(index: number) {
    setDomains((prev) => prev.filter((_, i) => i !== index));
  }

  function addDomain() {
    setDomains((prev) => [...prev, '']);
  }

  const saveMutation = useMutation({
    mutationFn: () =>
      apiFetch<BotDto>(`/api/bots/${botId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          settings: {
            allowedDomains: domains.map((d) => d.trim()),
            leadCapture,
            // Always sent alongside `leadCapture` (same "presence, not value" replace-per-key
            // semantics as `personaNote` below) so switching away from and back to 'on-intent'
            // never silently drops the chosen level.
            leadLevel,
          },
          // Top-level sibling of `settings`, not nested inside it — apps/api/src/routes/bots.ts
          // PATCH handler applies `personaNote` straight to the Bot row (Prechat-persona spec
          // §2). Always sent (like `allowedDomains`/`leadCapture` above): "presence, not value"
          // semantics mean an explicit '' clears the stored note.
          personaNote: personaNote.trim(),
        }),
      }),
    onSuccess: async () => {
      setSaveError(null);
      setSaved(true);
      await queryClient.invalidateQueries({ queryKey: ['bots', botId] });
      setTimeout(() => setSaved(false), 2000);
    },
    onError: (err: Error) => setSaveError(err.message),
  });

  return (
    <div className="flex max-w-2xl flex-col gap-6">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!hasDomainErrors) saveMutation.mutate();
        }}
        className="flex flex-col gap-6 rounded-xl border p-4"
      >
        <div className="flex flex-col gap-2">
          <Label htmlFor="persona-note">{t.botSettings.aboutLabel}</Label>
          <p className="text-sm text-muted-foreground">{t.botSettings.aboutHint}</p>
          <textarea
            id="persona-note"
            value={personaNote}
            onChange={(e) => setPersonaNote(e.target.value)}
            maxLength={PERSONA_NOTE_MAX}
            rows={3}
            placeholder={t.botSettings.aboutPlaceholder}
            className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
          />
          <span className="self-end text-xs text-muted-foreground">
            {personaNote.length} / {PERSONA_NOTE_MAX}
          </span>
        </div>

        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <Label>{t.botSettings.allowedDomainsLabel}</Label>
            <Button type="button" variant="outline" size="sm" onClick={addDomain}>
              {t.botSettings.addDomainCta}
            </Button>
          </div>
          <p className="text-sm text-muted-foreground">{t.botSettings.allowedDomainsHint}</p>
          {domains.map((domain, i) => (
            <div key={i} className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <Input
                  aria-label={t.botSettings.domainAriaLabel({ n: i + 1 })}
                  value={domain}
                  onChange={(e) => updateDomain(i, e.target.value)}
                  placeholder={t.botSettings.domainPlaceholder}
                  aria-invalid={!!domainErrors[i]}
                />
                <Button type="button" variant="outline" size="sm" onClick={() => removeDomain(i)}>
                  {t.botSettings.removeCta}
                </Button>
              </div>
              {domainErrors[i] && (
                <span className="text-xs text-destructive">{domainErrors[i]}</span>
              )}
            </div>
          ))}
        </div>

        <div className="flex flex-col gap-2">
          <Label htmlFor="lead-capture">{t.botSettings.leadCaptureLabel}</Label>
          <Select
            value={leadCapture}
            onValueChange={(value) => setLeadCapture(value as LeadCaptureMode)}
          >
            <SelectTrigger id="lead-capture" className="w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {leadCaptureOptions.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <ul className="flex flex-col gap-1 text-sm text-muted-foreground">
            {leadCaptureOptions.map((opt) => (
              <li key={opt.value} className={opt.value === leadCapture ? 'text-foreground' : ''}>
                <span className="font-medium">{opt.label}:</span> {opt.blurb}
              </li>
            ))}
          </ul>
        </div>

        {leadCapture === 'on-intent' && (
          <div className="flex flex-col gap-2">
            <Label htmlFor="lead-level">{t.botSettings.leadLevelLabel}</Label>
            <Select
              value={leadLevel}
              onValueChange={(value) => setLeadLevel(value as LeadProactivenessLevel)}
            >
              <SelectTrigger id="lead-level" className="w-full sm:w-64">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {leadLevelOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <ul className="flex flex-col gap-1 text-sm text-muted-foreground">
              {leadLevelOptions.map((opt) => (
                <li key={opt.value} className={opt.value === leadLevel ? 'text-foreground' : ''}>
                  <span className="font-medium">{opt.label}:</span> {opt.blurb}
                </li>
              ))}
            </ul>
          </div>
        )}

        {saveError && <p className="text-sm text-destructive">{saveError}</p>}

        <div className="flex items-center gap-3">
          <Button type="submit" disabled={saveMutation.isPending || hasDomainErrors}>
            {saveMutation.isPending ? t.botSettings.savingCta : t.botSettings.saveCta}
          </Button>
          {saved && <span className="text-sm text-muted-foreground">{t.botSettings.savedLabel}</span>}
        </div>
      </form>

      <div className="flex flex-col gap-2 rounded-xl border p-4">
        <h2 className="text-sm font-semibold">{t.botSettings.embedSnippetHeading}</h2>
        <p className="text-sm text-muted-foreground">
          {t.botSettings.embedPastePrefix} <code className="rounded bg-muted px-1">&lt;/body&gt;</code>{' '}
          {t.botSettings.embedPasteSuffix}
        </p>
        <EmbedSnippet publicId={bot.publicId} />
        <a
          href={`${landingUrl()}/docs/install`}
          target="_blank"
          rel="noreferrer"
          className="w-fit text-sm font-medium text-primary hover:underline"
        >
          {t.onboarding.goLiveInstallGuidesLink}
        </a>
      </div>

      {/* bot-notifications design (2026-08-21) Task 5 — mounted above Danger zone, per spec
          §Dashboard, so a destructive action is never the last thing on the page above a
          routine settings card. */}
      <NotificationsCard botId={botId} />

      <DangerZone botId={botId} bot={bot} />
    </div>
  );
}

/** Prechat-persona spec §3 — the confirm dialog requires typing the bot's exact name before
 * the delete button unlocks, same friction as GitHub-style destructive confirms. `DELETE
 * /api/bots/:id` (apps/api/src/routes/bots.ts) already enqueues source + search-instance
 * teardown, so this just calls it and leaves. */
function DangerZone({ botId, bot }: { botId: string | undefined; bot: BotDto }) {
  const t = useT();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmName, setConfirmName] = useState('');
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const deleteMutation = useMutation({
    mutationFn: () => apiFetch<{ deleted: boolean }>(`/api/bots/${botId}`, { method: 'DELETE' }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['bots'] });
      navigate('/');
    },
    onError: (err: Error) => setDeleteError(err.message),
  });

  function openDialog() {
    setDeleteError(null);
    setConfirmName('');
    setDialogOpen(true);
  }

  return (
    <div className="flex flex-col gap-2 rounded-xl border border-destructive/30 p-4">
      <h2 className="text-sm font-semibold text-destructive">{t.botSettings.dangerZoneHeading}</h2>
      <p className="text-sm text-muted-foreground">{t.botSettings.dangerZoneDescription}</p>
      <div>
        <Button type="button" variant="destructive" size="sm" onClick={openDialog}>
          {t.botSettings.deleteBotCta}
        </Button>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.botSettings.deleteBotDialogTitle({ name: bot.name })}</DialogTitle>
            <DialogDescription>
              {t.botSettings.dangerZoneDialogDescriptionPrefix}
              <span className="font-medium text-foreground">{bot.name}</span>
              {t.botSettings.dangerZoneDialogDescriptionSuffix}
            </DialogDescription>
          </DialogHeader>
          <Input
            aria-label={t.botSettings.confirmBotNameAriaLabel}
            value={confirmName}
            onChange={(e) => setConfirmName(e.target.value)}
            placeholder={bot.name}
            autoFocus
          />
          {deleteError && <p className="text-[13px] text-destructive">{deleteError}</p>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
              {t.botSettings.cancelCta}
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={confirmName !== bot.name || deleteMutation.isPending}
              onClick={() => deleteMutation.mutate()}
            >
              {deleteMutation.isPending ? t.botSettings.deletingCta : t.botSettings.deleteBotCta}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
